import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpEvent, HttpRequest, HttpErrorResponse } from '@angular/common/http';

import { Observable, of, throwError } from 'rxjs';

import { LoginBoxDialogService } from 'dialogs/loginbox';
import { MessageBoxDialogService } from 'dialogs/messagebox';
import { SplashService } from 'dialogs/splash';

import { AuthService } from 'services/auth/auth.service';
import { ErrorService } from 'services/error/error.service';
import { UnsavedChangesService } from 'routes/routes.guard';
import { catchError, switchMap } from 'rxjs/operators';
import { UserService } from 'services/user/user.service';
import { RevalidationInfo } from 'services/auth/auth.service.class';

@Injectable({
    providedIn: 'root',
})
export class AuthTokenInterceptor implements HttpInterceptor {
    constructor(
        private authSvc: AuthService,
        private errorSvc: ErrorService,
        private loginDlgSvc: LoginBoxDialogService,
        private msgBoxSvc: MessageBoxDialogService,
        private splashSvc: SplashService,
        private unsavedChangesService: UnsavedChangesService,
        private userSvc: UserService
    ) {}

    // tslint:disable-next-line: no-any
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const self = this;

        const authToken = self.userSvc?.User?.AuthToken;

        let headers = req.headers;

        if (authToken) {
            headers = headers.append('AuthToken', authToken);
        }

        const clonedReq = req.clone({ headers });

        return next.handle(clonedReq).pipe(
            catchError((err: HttpErrorResponse) => {
                const errmsg = this.GetErrorMessage(err);

                const recoverable =
                    (err && err.status === 401 && ['Session has expired.', 'No user information found.'].includes(errmsg)) ||
                    (err &&
                        err.status === 500 &&
                        err.error &&
                        !(err.error instanceof Blob) &&
                        errmsg.includes('Session has expired.'));

                if (recoverable) {
                    // The authtoken is dead on a refresh attempt. We might as well cancel authtoken refreshing for now.
                    self.authSvc.CancelAuthTokenRefreshing();

                    // Now, allow the user to reauthenticate in place if they wish.
                    return self.loginDlgSvc
                        .Open({
                            TitleText: 'Session Has Expired',
                            MessageText:
                                `We're sorry, but your current session to the system has expired due to ` +
                                `inactivity or network disconnection. You may continue using the system by re-entering your credentials and will remain on this page.<br /><br />If you would like to logout at this time, click the "Logout" button below.`,
                            OKText: 'Login',
                            CancelText: 'Cancel - Logout',
                            Username: self.userSvc.User?.IsImpersonation
                                ? self.userSvc.User?.ImpersonatorUsername || ''
                                : self.userSvc.User?.AccountName || '',
                            ImpersonatedUsername: self.userSvc.User?.IsImpersonation ? self.userSvc.User?.AccountName : '',
                            width: '500px',
                        })
                        .pipe(
                            switchMap((response) => {
                                if (!response) {
                                    // User cancelled. First, disable any pending unsaved changes
                                    self.unsavedChangesService.ClearTracking();

                                    // Now logout.
                                    self.authSvc.Logout(false);

                                    return new Observable<HttpEvent<any>>();
                                }

                                // Attempt to revalidate the user's session using the provided credentials.
                                return self.authSvc
                                    .RevalidateSession(response.Username, response.Password, response.ImpersonatedUsername)
                                    .pipe(
                                        catchError((revalerr) => {
                                            // Retrieve the error message for returning.
                                            const errmsg =
                                                self.errorSvc.RetrieveServiceError(revalerr) ||
                                                'The system was not able to provide a valid access token at this time.';

                                            return of<RevalidationInfo>({
                                                Error: errmsg,
                                                UserInfo: null,
                                            } as RevalidationInfo);
                                        }),
                                        switchMap((info) => {
                                            // We have a response packet. Return that via this dialog handler.
                                            if (info?.Error || !info.UserInfo?.AuthToken) {
                                                const errmsg =
                                                    info?.Error ||
                                                    'The system was not able to provide a valid access token at this time.';

                                                // User hit an error. Show the error, then we'll have to bail out.
                                                self.msgBoxSvc
                                                    .Open({
                                                        TitleText: 'Revalidation Error',
                                                        MessageText:
                                                            `An error was detected during your attempt to revalidate your session:<br /><br /><em>` +
                                                            `${errmsg}</em><br /><br />Your session cannot be revalidated. You will now be returned to the login page. We apologize for the inconvenience.`,
                                                        OKText: 'OK',
                                                    })
                                                    .subscribe((_) => {
                                                        // First, disable any pending unsaved changes
                                                        self.unsavedChangesService.ClearTracking();

                                                        // Now logout.
                                                        self.authSvc.Logout(false);
                                                    });

                                                return new Observable<HttpEvent<any>>();
                                            }

                                            // The user has logged back in successfully. Update the authtoken with what we just received.
                                            self.userSvc.UpdateAuthToken(info.UserInfo.AuthToken);

                                            // Toast successful revalidation
                                            self.splashSvc.CloseSplash();
                                            self.splashSvc.SnackbarSuccess('Session revalidated successfully.', 6000);

                                            // Clone the original request, then replay.
                                            const rheaders = req.headers.append('AuthToken', info.UserInfo.AuthToken);

                                            const retryReq = req.clone({ headers: rheaders });

                                            return next.handle(retryReq);
                                        })
                                    );
                            })
                        );
                } else {
                    // Is it a Blob error? If so, rethrow it.
                    if (err.error instanceof Blob) {
                        return throwError(err);
                    }

                    // Check for database issues.
                    if (errmsg && errmsg.includes('Execution Timeout Expired')) {
                        // Yup, it's in the database. Clean up the error and pass it along.
                        return throwError(
                            'An error occurred in the database which prevented the request from succeeding. Please wait and try again.'
                        );
                    }

                    // Nope. Just pass it along.
                    return throwError(err);
                }
            })
        );
    }

    GetErrorMessage(error: HttpErrorResponse): string {
        let body = error && error.error;
        let msg = '';

        if (body && body.errors && typeof body.errors === 'object') {
            // tslint:disable-next-line: no-any
            const errobj = body.errors as any;
            const errkeys = Object.keys(errobj);

            msg = errkeys
                .map((k) => {
                    return errobj[k].toString();
                })
                .join('|');
        } else if (body && body.error) {
            body = body.error;

            msg = body.ExceptionMessage || body.Message || body.message || body || 'An unknown error has occurred.';
        } else {
            msg = body?.ExceptionMessage || body?.Message || body?.message || body || 'An unknown error has occurred.';
        }

        return msg;
    }
}
